"use client";

import { useEffect, useState } from "react";
import { clsx } from "@/lib/clsx";

/**
 * @param onPhoto The hero has a background photograph, so the card goes
 *   translucent over it rather than sitting on it as a solid white slab.
 *   Off by default: on plain paper the solid `--card` white is correct.
 */
export function LeverCard({ onPhoto = false }: { onPhoto?: boolean }) {
  const [rotated, setRotated] = useState(false);

  useEffect(() => {
    // Replicate the load-in animation from the mockup
    const timer = setTimeout(() => {
      setRotated(true);
    }, 250);
    return () => clearTimeout(timer);
  }, []);

  return (
    // `.lever-card` used to lead this list and was defined in no stylesheet —
    // dropped for the same reason ADR-0007 dropped `.lever-stage`, and because
    // leaving it beside `.lever-card-glass` reads as if it were the one styling
    // the card.
    <div
      className={clsx(
        "rounded-sm border border-line p-[30px] pb-6",
        onPhoto ? "lever-card-glass" : "bg-card",
      )}
    >
      <div className="flex items-center gap-2.5">
        <span className="h-[2px] w-[30px] bg-brass" />
        <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-ink-60">
          Fig. 01 · Mechanical advantage
        </span>
      </div>
      
      <svg
        className="mt-1.5 block w-full h-auto"
        viewBox="0 0 440 230"
        role="img"
        aria-label="Schematic of a lever: a small annual operating cost on the long effort arm lifts an outsized annual saving on the short load arm."
      >
        <defs>
          <linearGradient id="brassGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#CDA64E" />
            <stop offset="1" stopColor="#8C6520" />
          </linearGradient>
          <linearGradient id="outGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#DBB662" />
            <stop offset="1" stopColor="#B5862F" />
          </linearGradient>
        </defs>
        
        {/* Baseline ruler */}
        <line
          x1="40"
          y1="186"
          x2="406"
          y2="186"
          stroke="#16202E"
          strokeOpacity="0.22"
          strokeWidth="1"
        />
        <g stroke="#16202E" strokeOpacity="0.15" strokeWidth="1">
          <line x1="64" y1="182" x2="64" y2="186" />
          <line x1="93" y1="182" x2="93" y2="186" />
          <line x1="122" y1="182" x2="122" y2="186" />
          <line x1="151" y1="182" x2="151" y2="186" />
          <line x1="180" y1="182" x2="180" y2="186" />
          <line x1="209" y1="182" x2="209" y2="186" />
          <line x1="238" y1="182" x2="238" y2="186" />
          <line x1="299" y1="182" x2="299" y2="186" />
          <line x1="328" y1="182" x2="328" y2="186" />
          <line x1="357" y1="182" x2="357" y2="186" />
          <line x1="386" y1="182" x2="386" y2="186" />
        </g>
        
        {/* Contact shadow */}
        <ellipse cx="270" cy="189" rx="32" ry="3.4" fill="#16202E" opacity="0.10" />
        
        {/* Pivot construction line */}
        <line
          x1="270"
          y1="150"
          x2="270"
          y2="186"
          stroke="#16202E"
          strokeOpacity="0.13"
          strokeWidth="1"
        />
        
        {/* Fulcrum (knife-edge, two-tone) */}
        <path
          d="M270 149 L286 186 L254 186 Z"
          fill="url(#brassGrad)"
          stroke="#7C5A1C"
          strokeWidth="0.6"
          strokeLinejoin="round"
        />
        <circle cx="270" cy="149.5" r="2" fill="#6E4F18" />
        
        {/* Dimension lines: effort arm vs load arm */}
        <g stroke="#16202E" strokeOpacity="0.36" strokeWidth="1">
          <line x1="64" y1="206" x2="266" y2="206" />
          <line x1="64" y1="202" x2="64" y2="210" />
          <line x1="266" y1="202" x2="266" y2="210" />
          <line x1="274" y1="206" x2="356" y2="206" />
          <line x1="274" y1="202" x2="274" y2="210" />
          <line x1="356" y1="202" x2="356" y2="210" />
        </g>
        
        <text
          x="165"
          y="222"
          textAnchor="middle"
          fontFamily="var(--font-mono), monospace"
          fontSize="8.5"
          letterSpacing="1.6"
          fill="#3A4656"
        >
          EFFORT ARM
        </text>
        <text
          x="315"
          y="222"
          textAnchor="middle"
          fontFamily="var(--font-mono), monospace"
          fontSize="8.5"
          letterSpacing="1.6"
          fill="#3A4656"
        >
          LOAD ARM
        </text>
        
        {/* Beam (settles on load) */}
        <g
          style={{
            transformBox: "view-box",
            transformOrigin: "270px 146px",
            transform: rotated ? "rotate(-6deg)" : "rotate(3deg)",
            transition: "transform 1.1s cubic-bezier(0.22, 1, 0.36, 1)",
          }}
        >
          <rect x="58" y="143" width="320" height="6" rx="3" fill="#16202E" />
          <g stroke="#D9B25E" strokeOpacity="0.5" strokeWidth="1">
            <line x1="90" y1="139.5" x2="90" y2="143" />
            <line x1="122" y1="139.5" x2="122" y2="143" />
            <line x1="154" y1="139.5" x2="154" y2="143" />
            <line x1="186" y1="139.5" x2="186" y2="143" />
            <line x1="218" y1="139.5" x2="218" y2="143" />
            <line x1="250" y1="139.5" x2="250" y2="143" />
            <line x1="282" y1="139.5" x2="282" y2="143" />
            <line x1="314" y1="139.5" x2="314" y2="143" />
            <line x1="346" y1="139.5" x2="346" y2="143" />
          </g>
          
          {/* Small input, far out on the long arm */}
          <circle cx="64" cy="146" r="5" fill="#16202E" />
          <circle cx="64" cy="146" r="5" fill="none" stroke="#B5862F" strokeWidth="1" />
          
          {/* Large output, near the pivot on the short arm */}
          <circle cx="350" cy="146" r="12" fill="url(#outGrad)" stroke="#8C6520" strokeWidth="0.8" />
        </g>
      </svg>
      
      <div className="mt-3.5 flex items-end justify-between border-t border-line pt-3.5">
        <div className="flex flex-col">
          <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink-60">Input</span>
          <b className="mt-1 font-display text-[17px] font-semibold text-ink">&lt; $800K&nbsp;/&nbsp;yr</b>
        </div>
        <div className="font-mono text-[15px] font-semibold tracking-[0.02em] text-brass-deep pb-0.5">
          &asymp; 1,750&times;
        </div>
        <div className="flex flex-col text-right">
          <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink-60">Output</span>
          <b className="mt-1 font-display text-[17px] font-semibold text-ink">$1.4B&nbsp;/&nbsp;yr</b>
        </div>
      </div>
      <div className="mt-3 font-mono text-[10px] leading-[1.55] tracking-[0.03em] text-ink-60">
        Virginia ORM: annual operating cost vs. estimated annual savings
      </div>
    </div>
  );
}
